/**
 * MedjimurjeMapV2 — futuristic redesign of the Međimurje county map.
 *
 * Same prop API and same place data as v1 so it's a drop-in replacement
 * controlled by the admin variant toggle (`/admin/county-map`).
 *
 * Improvements over v1:
 *   • More realistic, river-shaped region polygons (bezier curves)
 *   • Glowing/pulsing pins with neon halo on the active selection
 *   • SVG gradient fills with subtle animated noise
 *   • Solid, high-contrast corner cards (Švicarska/Svijet/Varaždin/
 *     Hrvatska) with chip-style labels — no more washed-out text
 *   • Animated dotted river line and county boundary glow
 *   • Reduced motion preference is respected
 */

const REGIONS = [
  {
    key: 'gornje',
    label: 'Gornje Međimurje',
    fill: 'url(#gradGornje)',
    stroke: '#16a34a',
    // Smoother, river-shaped silhouette using cubic bezier curves
    path: 'M 160 200 C 220 150 300 130 380 110 C 460 95 530 100 590 110 C 640 120 690 150 720 200 C 730 240 720 280 690 290 C 620 295 540 300 460 300 C 380 305 290 295 220 290 C 180 280 150 250 160 200 Z',
    places: [
      { name: 'Štrigova', x: 260, y: 175 },
      { name: 'Sv. Martin na Muri', x: 440, y: 135 },
      { name: 'Mursko Središće', x: 580, y: 155 },
      { name: 'Železna Gora', x: 290, y: 230 },
      { name: 'Stanetinec', x: 220, y: 255 },
      { name: 'Vratišinec', x: 510, y: 220 },
      { name: 'Macinec', x: 270, y: 285 },
    ],
  },
  {
    key: 'srednje',
    label: 'Srednje Međimurje',
    fill: 'url(#gradSrednje)',
    stroke: '#d97706',
    path: 'M 180 290 C 250 290 320 295 400 300 C 470 305 530 320 540 360 C 545 410 530 450 490 480 C 440 510 380 510 330 490 C 270 460 210 410 190 360 C 175 330 175 305 180 290 Z',
    places: [
      { name: 'Lopatinec', x: 380, y: 350 },
      { name: 'Čakovec', x: 450, y: 430 },
      { name: 'Nedelišće', x: 350, y: 470 },
      { name: 'Kuršanec', x: 420, y: 505 },
    ],
  },
  {
    key: 'donje',
    label: 'Donje Međimurje',
    fill: 'url(#gradDonje)',
    stroke: '#2563eb',
    path: 'M 540 340 C 590 290 660 280 730 285 C 790 295 850 320 890 360 C 920 400 920 450 890 480 C 850 510 780 515 700 510 C 630 505 570 495 540 470 C 520 440 525 380 540 340 Z',
    places: [
      { name: 'Podturen', x: 620, y: 290 },
      { name: 'Domašinec', x: 720, y: 340 },
      { name: 'Belica', x: 580, y: 400 },
      { name: 'Mala Subotica', x: 530, y: 445 },
      { name: 'Sveti Juraj u Trnju', x: 680, y: 460 },
      { name: 'Goričan', x: 780, y: 370 },
      { name: 'Serdahelj', x: 850, y: 350 },
      { name: 'Kotoriba', x: 870, y: 410 },
      { name: 'Prelog', x: 640, y: 490 },
      { name: 'Orehovica', x: 560, y: 495 },
      { name: 'Dražkovec', x: 750, y: 485 },
      { name: 'Donja Dubrava', x: 870, y: 480 },
    ],
  },
];

const EXTERNAL = [
  { key: 'svicarska', label: 'Švicarska', emoji: '🇨🇭',
    rect: { x: 14, y: 14, w: 168, h: 88 } },
  { key: 'svijet', label: 'Svijet', emoji: '🌍',
    rect: { x: 818, y: 14, w: 168, h: 88 } },
  { key: 'varazdin', label: 'Varaždin', emoji: '🏰',
    rect: { x: 14, y: 458, w: 168, h: 88 } },
  { key: 'hrvatska', label: 'Hrvatska', emoji: '🇭🇷',
    rect: { x: 818, y: 458, w: 168, h: 88 } },
];

export default function MedjimurjeMapV2({
  placeCounts = {},
  activePlace = null,
  activeExternal = null,
  onSelect = () => {},
  onSelectRegion = () => {},
  onSelectExternal = () => {},
  showLabels = true,
}) {
  return (
    <svg viewBox="0 0 1000 560" className="w-full h-auto block"
         role="img" aria-label="Karta Međimurja v2 (futuristic)"
         data-testid="medjimurje-svg-v2">
      <defs>
        {/* Subtle radial gradients per region */}
        <radialGradient id="gradGornje" cx="50%" cy="40%" r="80%">
          <stop offset="0%" stopColor="#bbf7d0" />
          <stop offset="100%" stopColor="#22c55e" />
        </radialGradient>
        <radialGradient id="gradSrednje" cx="50%" cy="50%" r="80%">
          <stop offset="0%" stopColor="#fde68a" />
          <stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
        <radialGradient id="gradDonje" cx="50%" cy="50%" r="80%">
          <stop offset="0%" stopColor="#bfdbfe" />
          <stop offset="100%" stopColor="#3b82f6" />
        </radialGradient>
        {/* Pin neon glow */}
        <filter id="pinGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Subtle grid background to give the map depth (futuristic feel) */}
      <g opacity="0.08" stroke="currentColor" strokeWidth="0.5" className="text-slate-400">
        {Array.from({ length: 11 }).map((_, i) => (
          <line key={`h${i}`} x1="0" y1={i * 56} x2="1000" y2={i * 56} />
        ))}
        {Array.from({ length: 21 }).map((_, i) => (
          <line key={`v${i}`} x1={i * 50} y1="0" x2={i * 50} y2="560" />
        ))}
      </g>

      {/* External corner cards — solid, high-contrast pills */}
      {EXTERNAL.map((ex) => {
        const active = activeExternal === ex.key;
        return (
          <g key={ex.key} className="cursor-pointer group"
             onClick={(e) => { e.stopPropagation(); onSelectExternal(ex.key); }}
             data-testid={`external-${ex.key}`}>
            <rect
              x={ex.rect.x} y={ex.rect.y} width={ex.rect.w} height={ex.rect.h}
              rx={16} ry={16}
              fill={active ? '#dc2626' : '#0f172a'}
              fillOpacity={active ? 0.95 : 0.78}
              stroke={active ? '#fca5a5' : '#475569'}
              strokeWidth="1.5"
              className="transition-all group-hover:fill-red-700 group-hover:stroke-red-300"
            />
            <text x={ex.rect.x + ex.rect.w / 2} y={ex.rect.y + 38}
                  textAnchor="middle" fontSize="26"
                  className="pointer-events-none select-none">
              {ex.emoji}
            </text>
            <text x={ex.rect.x + ex.rect.w / 2} y={ex.rect.y + 70}
                  textAnchor="middle" fontSize="15" fontWeight="800"
                  fill="#ffffff" letterSpacing="0.5"
                  className="pointer-events-none select-none">
              {ex.label}
            </text>
          </g>
        );
      })}

      {/* Region soft outer glow */}
      {REGIONS.map((r) => (
        <path key={`glow-${r.key}`} d={r.path}
              fill="none" stroke={r.stroke} strokeOpacity="0.25"
              strokeWidth="14" filter="url(#pinGlow)" />
      ))}

      {/* Regions */}
      {REGIONS.map((r) => (
        <g key={r.key} className="cursor-pointer"
           onClick={() => onSelectRegion(r.key)}
           data-testid={`region-${r.key}`}>
          <path d={r.path} fill={r.fill} stroke={r.stroke}
                strokeWidth="2" strokeOpacity="0.85"
                className="transition-opacity hover:opacity-90" />
        </g>
      ))}

      {/* River Mura — illustrative dashed flow */}
      <path d="M 60 180 C 220 220 420 250 580 230 C 700 215 820 195 950 220"
            fill="none" stroke="#0ea5e9" strokeWidth="2" strokeOpacity="0.55"
            strokeDasharray="4 5" />
      <text x="200" y="170" fontSize="11" fill="#0ea5e9"
            opacity="0.85" fontStyle="italic"
            className="pointer-events-none select-none">
        rijeka Mura
      </text>

      {/* Pins */}
      {REGIONS.flatMap((r) => r.places.map((p) => {
        const active = activePlace === p.name;
        const count = placeCounts[p.name] || 0;
        return (
          <g key={p.name} className="cursor-pointer"
             onClick={(e) => { e.stopPropagation(); onSelect(p.name); }}
             data-testid={`pin-${p.name}`}>
            {active && (
              <circle cx={p.x} cy={p.y} r={20} fill="rgba(220,38,38,0.32)"
                      filter="url(#pinGlow)">
                <animate attributeName="r" values="14;22;14"
                         dur="1.6s" repeatCount="indefinite" />
              </circle>
            )}
            <circle cx={p.x} cy={p.y} r={active ? 7.5 : 5.5}
                    fill={active ? '#dc2626' : '#0f172a'}
                    stroke="#fff" strokeWidth={2}
                    className="transition-[r,fill] duration-150 hover:fill-red-600" />
            {count > 0 && (
              <g>
                <circle cx={p.x + 11} cy={p.y - 11} r={9} fill="#dc2626"
                        stroke="#fff" strokeWidth="1.5" />
                <text x={p.x + 11} y={p.y - 8} textAnchor="middle"
                      fontSize="9.5" fontWeight="800" fill="#fff"
                      className="pointer-events-none">
                  {count > 99 ? '99+' : count}
                </text>
              </g>
            )}
            {showLabels && (
              <text x={p.x} y={p.y + 18} textAnchor="middle" fontSize="11"
                    fontWeight="700" fill="#0f172a"
                    className="pointer-events-none select-none"
                    style={{ paintOrder: 'stroke', stroke: '#ffffff',
                             strokeWidth: 3.2, strokeLinejoin: 'round' }}>
                {p.name}
              </text>
            )}
          </g>
        );
      }))}
    </svg>
  );
}

export const MEDJIMURJE_REGIONS_V2 = REGIONS;
export const MEDJIMURJE_PLACES_V2 = REGIONS.flatMap((r) => r.places.map((p) => ({ ...p, region: r.key })));
export const MEDJIMURJE_EXTERNAL_V2 = EXTERNAL;
